// Pressure-reactive colour: cage nodes measure how compressed their cells are,
// each vertex blends the strain of its 27 binding nodes, and a slow memory
// keeps a fading hand-print. Modelled on graphics-atlas' thickness-driven
// translucency term with thickness replaced by strain, and its decaying dye
// grid for the memory. Colour pairs are art direction, not a measured compound.
export const PAIRS = {
  base: { from: '#f7c6d9', to: '#8d6bc9' },
  lemon: { from: '#fff0a8', to: '#ff9a4a' },
  sky: { from: '#bfe3ff', to: '#ff9ad0' },
};
export class StrainColor {
  constructor(body, { rise = .08, fade = 2, gain = 3.2, cutoff = .02 } = {}) {
    this.body = body; this.rise = rise; this.fade = fade; this.gain = gain; this.cutoff = cutoff;
    this.nodeStrain = new Float64Array(body.count);
    this.nodeTets = Array.from({ length: body.count }, () => []);
    body.tets.forEach((tet, index) => { for (const v of tet.v) this.nodeTets[v].push(index); });
    this.items = []; this.activity = 0; this.peak = 0;
  }
  // Each deforming item gets a per-vertex memory and an attribute array.
  attach(item, bindings) {
    const count = bindings.length;
    const record = { item, bindings, memory: new Float32Array(count), dirty: true };
    this.items.push(record); return record;
  }
  measure() {
    const body = this.body, p = body.p;
    for (let i = 0; i < body.count; i++) {
      const tets = this.nodeTets[i]; let strain = 0;
      for (const t of tets) { const tet = body.tets[t]; strain += Math.max(0, 1 - body.volume(p, tet.v) / tet.rest); }
      this.nodeStrain[i] = tets.length ? strain / tets.length : 0;
    }
  }
  step(dt) {
    if (!Number.isFinite(dt) || dt <= 0) return false;
    dt = Math.min(dt, 1 / 30);
    this.measure();
    const up = 1 - Math.exp(-dt / this.rise), down = 1 - Math.exp(-dt / this.fade);
    let peak = 0, changed = false;
    for (const record of this.items) {
      const { bindings, memory } = record;
      for (let v = 0; v < bindings.length; v++) {
        const { indices, weights } = bindings[v]; let strain = 0;
        for (let k = 0; k < indices.length; k++) strain += this.nodeStrain[indices[k]] * weights[k];
        strain = Math.min(1, strain * this.gain);
        const previous = memory[v];
        const value = strain > previous ? previous + (strain - previous) * up : previous + (strain - previous) * down;
        // Below the cutoff the tint is invisible; end the fade exactly so the stage can idle.
        memory[v] = value < this.cutoff && strain < this.cutoff ? 0 : value;
        if (Math.abs(memory[v] - previous) > 1e-4) changed = true;
        peak = Math.max(peak, memory[v]);
      }
      if (changed) record.dirty = true;
    }
    this.peak = peak; this.activity = changed ? peak : 0;
    return changed;
  }
  reset() { for (const record of this.items) { record.memory.fill(0); record.dirty = true; } this.activity = 0; this.peak = 0; }
}
